'use client';
import usePrevious from '@repo/ui/src/hooks/usePrevious';
import React, { useState } from 'react';
import { AccordionContext, ID, OpenDrawer } from './_AccordionContext';

export interface AccordionProps {
  dark?: boolean;
  divider?: boolean;
  className?: string;
  children: React.ReactNode;
  allowZeroExpanded?: boolean;
  allowMultipleExpanded?: boolean;
  preExpanded?: OpenDrawer;
  onChange?: (openDrawers: OpenDrawer) => void;
  'data-testid'?: string;
}

const Accordion = ({
  className,
  children,
  allowZeroExpanded = false,
  allowMultipleExpanded = false,
  preExpanded = [],
  dark = false,
  divider = false,
  onChange,
  'data-testid': testId,
}: AccordionProps) => {
  const [openDrawer, setOpenDrawer] = useState<OpenDrawer>(
    allowMultipleExpanded ? preExpanded : preExpanded?.slice(0, 1),
  );
  const previousOpenDrawer = usePrevious(openDrawer);
  const getNewDrawers = React.useCallback(
    (currentDrawers: OpenDrawer, index: ID) => {
      const isOpen = currentDrawers.includes(index);
      if (isOpen) {
        if (currentDrawers.length === 1) {
          return allowZeroExpanded ? [] : currentDrawers;
        }
        return currentDrawers.filter((i) => i !== index);
      }

      return allowMultipleExpanded ? [...currentDrawers, index] : [index];
    },
    [allowMultipleExpanded, allowZeroExpanded],
  );
  const toggleDrawer = (index: ID) => {
    setOpenDrawer((curr) => {
      const newDrawers = getNewDrawers(curr, index);
      onChange?.(newDrawers);
      return newDrawers;
    });
  };

  return (
    <div 
      className={className} 
      data-testid={testId}
      role="tablist"
      aria-orientation="vertical"
    >
      <AccordionContext.Provider
        value={{ openDrawer, toggleDrawer, previousOpenDrawer, dark, divider }}
      >
        {children}
      </AccordionContext.Provider>
    </div>
  );
};

export { AccordionItem } from './_AccordionItem';

export default Accordion;
